import React from "react"
import styled from "styled-components"
import breakpoints from "@constants/breakpoints"
import BlockSlider from "@components/BlockSlider"
import ProductBlock from "@components/ProductBlock"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import { getIn } from "@utils/index"
import { MobileAndTabletOnly, DesktopOnly } from "@utils/MediaQuery"
import * as R from "ramda"

const Container = styled.div``

const MobileHero = styled.div`
  ${ImageHelperContainer} {
    height: calc(100vw * 1.33);

    @media ${breakpoints.tablet} {
      height: 100vw;
    }
  }
`

const Hero = styled.div`
  ${ImageHelperContainer} {
    height: calc(12 / 12 * 100vw * 0.49);
  }
`

const blockSliderStyles = `
  padding: 31px 0 44px;

  @media ${breakpoints.tablet} {
    padding-top: 104px;
    padding-bottom: 54px;
  }

  @media ${breakpoints.laptop} {
    padding-top: 154px;
    padding-bottom: 104px;
  }
`

export interface ProductProps {
  id: string
  slug: string
  productName: string
  variants: Array<{
    images: Array<string>
    price?: string
    compareAtPrice?: string
    marketPrices: any
  }>
  onClick?: (e: any) => void
}

interface FeaturedProductListProps {
  title: string
  titleShort: string
  mobileHero: string
  hero: string
  link: string
  linkText: string
  linkTextShort: string
  productList: Array<ProductProps>
}

const formatProducts = R.map(
  ({ id, slug, productName, variants, onClick }: ProductProps) => {
    const [variant] = variants

    const showCompareAtPrice =
      variant &&
      variant.price &&
      variant.compareAtPrice &&
      variant.price !== variant.compareAtPrice

    return {
      id,
      slug,
      title: productName,
      image: (variant && getIn(variant, "images.0.file.url")) || "",
      price: (variant && `$${variant.price}`) || "",
      compareAtPrice: showCompareAtPrice ? variant.compareAtPrice : undefined,
      marketPrices: variant && variant.marketPrices,
      onClick,
    }
  }
)

const FeaturedProductList: React.FC<FeaturedProductListProps> = ({
  title,
  titleShort,
  mobileHero,
  hero,
  link,
  linkText,
  linkTextShort,
  productList,
}) => {
  if (!hero || !mobileHero) {
    return null
  }

  return (
    <Container>
      <MobileAndTabletOnly>
        <MobileHero>
          <ImageHelper src={mobileHero} />
        </MobileHero>
      </MobileAndTabletOnly>
      <DesktopOnly>
        <Hero>
          <ImageHelper src={hero} />
        </Hero>
      </DesktopOnly>
      <BlockSlider
        title={title}
        titleShort={titleShort}
        to={link}
        toText={linkText}
        toTextShort={linkTextShort}
        data={formatProducts(productList)}
        renderBlock={(block) => (
          <ProductBlock
            slug={block.slug}
            image={block.image}
            title={block.title}
            subtitle={block.price}
            decoratedSubtitle={block.compareAtPrice}
            onClick={block.onClick}
          />
        )}
        containerStyles={blockSliderStyles}
      />
    </Container>
  )
}

export default FeaturedProductList
